import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
// ==========================================================================
// ✅ ASYNC THUNKS
// ==========================================================================
export const fetchEmployeePerformance = createAsyncThunk("employeePerformance/fetch", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/employee-performance/listing", payload, {
            Authorization: token || "",
        });
        if (response && "data" in response && response?.data) {
            const responseData = response.data;
            if (responseData?.success) {
                return responseData?.data;
            }
            return rejectWithValue(responseData?.message || "Failed to fetch employee performance");
        }
        return rejectWithValue("Failed to fetch employee performance");
    }
    catch (error) {
        console.error("Employee Performance API Error:", error);
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching employee performance");
    }
});
const getDefaultMonth = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    return `${mm}/${yyyy}`;
};
const initialState = {
    accountManagers: [],
    totalSummary: null,
    pagination: null,
    loading: false,
    error: null,
    month: getDefaultMonth(),
    amType: "all",
    search: "",
    page: 1,
    limit: 20,
    sortBy: "accounts", // the listing opens with the biggest portfolios (most accounts) first
    sortOrder: "desc",
};
// ==========================================================================
// ✅ SLICE
// ==========================================================================
const employeePerformanceSlice = createSlice({
    name: "employeePerformance",
    initialState,
    reducers: {
        setMonth: (state, action) => {
            state.month = action.payload;
            state.page = 1;
        },
        setAmType: (state, action) => {
            state.amType = action.payload;
            state.page = 1;
        },
        setSearch: (state, action) => {
            state.search = action.payload;
            state.page = 1; // any new search term restarts from page 1
        },
        setSortConfig: (state, action) => {
            state.sortBy = action.payload.sortBy;
            state.sortOrder = action.payload.sortOrder;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEmployeePerformance.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchEmployeePerformance.fulfilled, (state, action) => {
            state.loading = false;
            state.accountManagers = action.payload.accountManagers || [];
            state.totalSummary = action.payload.total_summary || null;
            state.pagination = action.payload.pagination || null;
            state.error = null;
        })
            .addCase(fetchEmployeePerformance.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch employee performance";
        });
    },
});
// ==========================================================================
// ✅ EXPORTS
// ==========================================================================
export const { setMonth, setAmType, setSearch, setSortConfig, setPage, clearError } = employeePerformanceSlice.actions;
// ==========================================================================
// ✅ SELECTORS
// ==========================================================================
export const selectEmployeePerformanceRows = (state) => state.employeePerformance.accountManagers;
export const selectEmployeePerformanceTotalSummary = (state) => state.employeePerformance.totalSummary;
export const selectEmployeePerformancePagination = (state) => state.employeePerformance.pagination;
export const selectEmployeePerformanceLoading = (state) => state.employeePerformance.loading;
export const selectEmployeePerformanceError = (state) => state.employeePerformance.error;
export const selectEmployeePerformanceMonth = (state) => state.employeePerformance.month;
export const selectEmployeePerformanceAmType = (state) => state.employeePerformance.amType;
export const selectEmployeePerformanceSearch = (state) => state.employeePerformance.search;
export const selectEmployeePerformancePage = (state) => state.employeePerformance.page;
export const selectEmployeePerformanceSortBy = (state) => state.employeePerformance.sortBy;
export const selectEmployeePerformanceSortOrder = (state) => state.employeePerformance.sortOrder;
export default employeePerformanceSlice.reducer;
